'use client'

import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { GradientButton } from '@/components/ui/gradient-button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  ArrowLeft,
  FileText,
  Scale,
  UserCheck,
  ShieldAlert,
  DollarSign,
  AlertTriangle,
  Copyright,
  Gavel,
  Mail,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  Shield,
  Info,
} from 'lucide-react'
import { Breadcrumb } from '@/components/breadcrumb'
import Link from 'next/link'

const termsHighlights = [
  {
    icon: Scale,
    title: 'Fair & Transparent',
    description: 'Clear terms with no hidden clauses or unexpected obligations',
    color: 'text-[#518BC4]',
    bgColor: 'bg-[#518BC4]/10',
  },
  {
    icon: ShieldAlert,
    title: 'Your Protection',
    description: 'Warranties and guarantees to protect your purchases',
    color: 'text-green-600',
    bgColor: 'bg-green-600/10',
  },
  {
    icon: Gavel,
    title: 'Legal Compliance',
    description: 'Fully compliant with applicable laws and regulations',
    color: 'text-[#7C1414]',
    bgColor: 'bg-[#7C1414]/10',
  },
]

const termsCategories = [
  {
    icon: DollarSign,
    title: 'Pricing & Payment',
    description: 'Terms related to product pricing, payment, and billing',
    content: [
      'All prices are in USD and subject to change without notice',
      'Prices do not include shipping, taxes, or customs duties unless stated',
      'Payment is due at the time of order placement',
      'We accept major credit cards, PayPal, wire transfer, and purchase orders (for approved accounts)',
      'Pricing errors: We reserve the right to cancel orders with incorrect pricing',
      'Volume discounts and special pricing available for qualified customers',
    ],
  },
  {
    icon: Shield,
    title: 'Product Warranties',
    description: 'Warranty coverage and limitations for products purchased',
    content: [
      'ProSoft Electronics provides a 30-day warranty on all products from the date of delivery',
      'Warranty covers defects in materials and workmanship under normal use',
      'Warranty does NOT cover: misuse, abuse, improper installation, or unauthorized modifications',
      'To claim warranty, contact us within 30 days with proof of purchase, description of defect, and valid test report',
      'Warranty remedies limited to repair, replacement, or refund at our discretion',
      'After the 30-day period, all warranty claims must be directed to the original manufacturer',
    ],
  },
  {
    icon: AlertTriangle,
    title: 'Liability & Disclaimers',
    description: 'Important limitations and disclaimers of liability',
    content: [
      'Products sold "AS IS" except as expressly warranted by manufacturer',
      'We are not liable for consequential, incidental, or indirect damages',
      'Our liability is limited to the purchase price of the product',
      'Technical specifications subject to change by manufacturers',
      'Product images and descriptions are for reference; actual products may vary',
      'We are not responsible for typographical errors in product listings',
    ],
  },
  {
    icon: Copyright,
    title: 'Intellectual Property',
    description: 'Protection of trademarks, copyrights, and proprietary content',
    content: [
      'All content on this website is protected by copyright and trademark laws',
      'ProSoft Electronics logo, name, and branding are our trademarks',
      'Product names and logos are trademarks of their respective manufacturers',
      'You may not reproduce, distribute, or create derivative works without permission',
      'Product datasheets and technical documents are property of manufacturers',
      'User-submitted content may be used by us for marketing and promotional purposes',
    ],
  },
  {
    icon: Gavel,
    title: 'Dispute Resolution',
    description: 'How disputes are handled and governing law',
    content: [
      'These terms governed by the laws of the State of California, USA',
      'Disputes resolved through binding arbitration (except small claims court)',
      'Arbitration conducted by American Arbitration Association (AAA)',
      'Class action waiver: disputes must be resolved individually',
      'Venue for any legal proceedings: San Francisco County, California',
      'Informal dispute resolution required before formal arbitration',
    ],
  },
]

const userConduct = [
  {
    allowed: true,
    title: 'Acceptable Use',
    items: [
      'Browse and purchase products for legitimate business purposes',
      'Use website features for their intended purpose',
      'Provide honest product reviews and feedback',
      'Contact support for assistance and inquiries',
      'Share product information with proper attribution',
    ],
  },
  {
    allowed: false,
    title: 'Prohibited Activities',
    items: [
      'Attempting to gain unauthorized access to systems',
      'Scraping, harvesting, or copying website content without permission',
      'Submitting false or fraudulent information',
      'Using the site for any illegal or unauthorized purpose',
      'Interfering with or disrupting website functionality',
      'Impersonating any person or entity',
    ],
  },
]

const termsFaqs = [
  {
    question: 'Can I cancel my order after placing it?',
    answer: 'Orders cannot be cancelled before shipping. If you attempt to cancel after placing an order, we will charge a reshelving cost to cover handling and restocking expenses. Refunds are processed in 10-15 business days. We do not offer credit card payments yet.',
  },
  {
    question: 'What if the product I receive is different from what I ordered?',
    answer: 'If you receive the wrong product due to our error, we will cover all return shipping costs and ship the correct product at no additional charge. Contact us within 48 hours of delivery with your order number and photos of the received product. We will arrange for pickup of the incorrect item.',
  },
  {
    question: 'Are product specifications guaranteed to be accurate?',
    answer: 'While we strive for accuracy, product specifications are provided by manufacturers and may change without notice. We recommend verifying critical specifications with our technical team before ordering. We are not liable for minor discrepancies in specifications or product descriptions.',
  },
  {
    question: 'Do you offer any guarantees on delivery times?',
    answer: 'Delivery estimates are provided in good faith but are not guaranteed. Actual delivery times may vary due to carrier delays, weather, customs processing (for international orders), or other unforeseen circumstances. We are not liable for delays beyond our reasonable control.',
  },
  {
    question: 'What happens if I dispute a charge?',
    answer: 'If you dispute a charge with your credit card company, we reserve the right to suspend your account pending resolution. Please contact us first to resolve any billing issues. Fraudulent chargebacks may result in permanent account termination and potential legal action to recover costs.',
  },
  {
    question: 'Can these terms be modified?',
    answer: 'Yes, we reserve the right to modify these Terms of Service at any time. Changes will be posted on this page with an updated "Last Modified" date. Continued use of our services after changes constitutes acceptance of the modified terms. We recommend reviewing this page periodically.',
  },
  {
    question: 'What if I violate these terms?',
    answer: 'Violations may result in account suspension or termination, order cancellation, and/or legal action. We reserve the right to refuse service to anyone for any reason. Serious violations (fraud, hacking, etc.) will be reported to appropriate authorities.',
  },
  {
    question: 'How do warranty claims work?',
    answer: 'ProSoft Electronics provides a 30-day warranty from the date of delivery. For warranty claims within this period, contact us with your order number, product details, description of the defect, and a valid test report. We will arrange repair, replacement, or refund at our discretion. After the 30-day period, warranty claims must be directed to the original manufacturer according to their warranty terms.',
  },
  {
    question: 'What if my product arrives damaged?',
    answer: 'Our orders are shipped under Ex Works terms, which means we are not held liable for any damage that occurs during shipping or delivery. The risk of loss or damage transfers to you once the goods leave our premises. We recommend purchasing appropriate insurance coverage for your shipments.',
  },
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
}

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Breadcrumb
        items={[
          { href: '/', label: 'Home' },
          { label: 'Terms of Service', current: true },
        ]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#518BC4] to-[#7C1414] text-white py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              href="/"
              className="inline-flex items-center text-white/80 hover:text-white transition-colors mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <FileText className="h-8 w-8" />
              </div>
              <Badge variant="secondary" className="bg-white/10 text-white border-white/20">
                Legal Agreement
              </Badge>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
            <p className="text-xl text-white/90 max-w-3xl mb-6">
              Please read these terms carefully before using our services. By accessing or using ProSoft Electronics,
              you agree to be bound by these terms and conditions.
            </p>
            <div className="flex items-center gap-2 text-white/80">
              <Clock className="h-5 w-5" />
              <span className="text-sm">Last Updated: October 16, 2025</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Terms Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <Badge className="bg-[#518BC4]/10 text-[#518BC4] border-[#518BC4]/20 mb-4">
                Our Commitment
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Terms Overview</h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Fair, transparent terms designed to protect both customers and our business
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {termsHighlights.map((highlight, index) => {
                const IconComponent = highlight.icon
                return (
                  <motion.div key={index} variants={itemVariants}>
                    <Card className="h-full border border-slate-200 shadow-sm hover:shadow-lg transition-all">
                      <CardContent className="p-8 text-center">
                        <div className={`w-16 h-16 ${highlight.bgColor} rounded-full flex items-center justify-center mx-auto mb-6`}>
                          <IconComponent className={`h-8 w-8 ${highlight.color}`} />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-3">{highlight.title}</h3>
                        <p className="text-slate-600">{highlight.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Acceptance of Terms */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="max-w-4xl mx-auto">
              <Card className="border-2 border-[#518BC4] shadow-lg">
                <CardHeader className="bg-gradient-to-r from-[#518BC4]/10 to-[#7C1414]/10">
                  <CardTitle className="flex items-center gap-3 text-2xl">
                    <Info className="h-8 w-8 text-[#518BC4]" />
                    Agreement to Terms
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8">
                  <div className="space-y-4 text-slate-700 leading-relaxed">
                    <p>
                      By accessing and using the ProSoft Electronics website, placing orders, or utilizing any of our
                      services, you acknowledge that you have read, understood, and agree to be bound by these Terms of
                      Service and our Privacy Policy.
                    </p>
                    <p>
                      If you do not agree to these terms, you must not use our website or services. These terms
                      constitute a legally binding agreement between you (the "Customer" or "User") and ProSoft
                      Electronics (the "Company," "we," "us," or "our").
                    </p>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-6 w-6 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-bold text-amber-900 mb-1">Important Notice</h4>
                          <p className="text-sm text-amber-800">
                            These terms include important provisions such as a binding arbitration clause and class
                            action waiver. Please review the entire document carefully, particularly the sections on
                            Liability Limitations and Dispute Resolution.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Detailed Terms Categories */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <Badge className="bg-[#7C1414]/10 text-[#7C1414] border-[#7C1414]/20 mb-4">
                Detailed Terms
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Terms & Conditions</h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Comprehensive terms covering all aspects of our business relationship
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {termsCategories.map((category, index) => {
                const IconComponent = category.icon
                return (
                  <motion.div key={index} variants={itemVariants}>
                    <Card className="h-full border border-slate-200 shadow-sm hover:shadow-lg transition-all">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-[#518BC4]/10 rounded-xl flex items-center justify-center">
                            <IconComponent className="h-6 w-6 text-[#518BC4]" />
                          </div>
                          <div>
                            <div className="text-lg">{category.title}</div>
                            <div className="text-sm font-normal text-slate-600">{category.description}</div>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3">
                          {category.content.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start gap-2">
                              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-slate-600">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* User Conduct */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <Badge className="bg-[#518BC4]/10 text-[#518BC4] border-[#518BC4]/20 mb-4">
                User Responsibilities
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Acceptable Use Policy</h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Guidelines for appropriate use of our website and services
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {userConduct.map((conduct, index) => (
                <Card key={index} className={`border-2 ${conduct.allowed ? 'border-green-500' : 'border-red-500'} shadow-lg`}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      {conduct.allowed ? (
                        <CheckCircle className="h-8 w-8 text-green-500" />
                      ) : (
                        <XCircle className="h-8 w-8 text-red-500" />
                      )}
                      <span>{conduct.title}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {conduct.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-3">
                          {conduct.allowed ? (
                            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                          )}
                          <span className="text-slate-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Availability & Accuracy */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <Badge className="bg-[#7C1414]/10 text-[#7C1414] border-[#7C1414]/20 mb-4">
                  Important Disclaimers
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Product Availability & Accuracy
                </h2>
              </div>

              <Card className="border border-slate-200 shadow-lg">
                <CardContent className="p-8 space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-6 w-6 text-orange-500" />
                      Inventory & Availability
                    </h3>
                    <p className="text-slate-600 mb-3">
                      Product availability is subject to change without notice. We strive to maintain accurate inventory
                      levels, but cannot guarantee product availability until your order is confirmed and payment is
                      processed.
                    </p>
                    <ul className="space-y-2 ml-6">
                      <li className="text-sm text-slate-600">• Inventory levels updated regularly but not in real-time</li>
                      <li className="text-sm text-slate-600">
                        • Out-of-stock items may be backordered or substituted with customer approval
                      </li>
                      <li className="text-sm text-slate-600">
                        • Lead times for special orders and backorders vary by manufacturer
                      </li>
                    </ul>
                  </div>

                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <Info className="h-6 w-6 text-blue-500" />
                      Product Information Accuracy
                    </h3>
                    <p className="text-slate-600 mb-3">
                      While we make every effort to ensure accuracy, product descriptions, specifications, images, and
                      pricing may contain errors or be outdated.
                    </p>
                    <ul className="space-y-2 ml-6">
                      <li className="text-sm text-slate-600">
                        • Technical specifications sourced from manufacturers and subject to change
                      </li>
                      <li className="text-sm text-slate-600">
                        • Product images are for reference; actual products may vary in appearance
                      </li>
                      <li className="text-sm text-slate-600">
                        • We reserve the right to correct errors and update information without notice
                      </li>
                    </ul>
                  </div>

                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <DollarSign className="h-6 w-6 text-green-500" />
                      Pricing Errors
                    </h3>
                    <p className="text-slate-600">
                      In the event of a pricing error, we reserve the right to cancel the affected order and refund any
                      payment received. We will notify you of such errors and offer you the option to purchase at the
                      correct price.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <Badge className="bg-[#518BC4]/10 text-[#518BC4] border-[#518BC4]/20 mb-4">
                Common Questions
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Terms of Service FAQ</h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Frequently asked questions about our terms and conditions
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <Card className="border border-slate-200 shadow-lg">
                <CardContent className="p-8">
                  <Accordion type="single" collapsible className="w-full space-y-4">
                    {termsFaqs.map((faq, index) => (
                      <AccordionItem
                        key={index}
                        value={`item-${index}`}
                        className="border border-slate-200 rounded-lg px-6 data-[state=open]:bg-slate-50"
                      >
                        <AccordionTrigger className="text-left hover:no-underline py-4">
                          <span className="text-lg font-semibold text-slate-900 pr-4">{faq.question}</span>
                        </AccordionTrigger>
                        <AccordionContent className="text-slate-600 pb-4 leading-relaxed">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <Card className="border-0 shadow-xl bg-gradient-to-r from-slate-50 to-slate-100 max-w-4xl mx-auto">
            <CardContent className="p-12">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Questions About These Terms?</h3>
                <p className="text-slate-600">Contact our legal team for clarification or assistance</p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#518BC4]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="h-7 w-7 text-[#518BC4]" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 mb-1">Legal Department</div>
                    <a href="mailto:legal@prosoftelectronics.com" className="text-[#518BC4] hover:underline">
                      legal@prosoftelectronics.com
                    </a>
                    <div className="text-sm text-slate-600">Business inquiries only</div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#7C1414]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Scale className="h-7 w-7 text-[#7C1414]" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 mb-1">Compliance Officer</div>
                    <a href="mailto:compliance@prosoftelectronics.com" className="text-[#7C1414] hover:underline">
                      compliance@prosoftelectronics.com
                    </a>
                    <div className="text-sm text-slate-600">Regulatory & compliance</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Related Legal Information</h2>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Review our complete legal documentation for a full understanding of our policies
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <GradientButton size="lg" variant="blue-red" className="group" asChild>
                <Link href="/legal/privacy" className="inline-flex items-center">
                  Privacy Policy
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </GradientButton>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-slate-900 bg-transparent"
                asChild
              >
                <Link href="/legal/cookies">
                  <FileText className="mr-2 h-5 w-5" />
                  Cookie Policy
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
